import { EventEmitter, inject, Injectable } from '@angular/core';

import { ScheduleNActionService } from '../../../../+authenticated/+schedule/shared/schedule-actions/schedule-n-action.service';
import { DragType } from '../../../../enums';
import { DragDropConfig } from '../../config/drag-drop-config';
import { DragDropData, DropZoneData } from '../../models/drag-drop-data.model';

export const copyZoneSupport: Record<DragType, DragType[]> = {
  [DragType.SHIFT]: [DragType.SCHEDULE, DragType.OPEN_SHIFT, DragType.REQUIRED_SHIFT],
  [DragType.EVENT]: [DragType.EVENT],
  [DragType.SCHEDULE]: [DragType.SCHEDULE, DragType.OPEN_SHIFT, DragType.REQUIRED_SHIFT],
  [DragType.OPEN_SHIFT]: [DragType.OPEN_SHIFT, DragType.REQUIRED_SHIFT],
  [DragType.REQUIRED_SHIFT]: [DragType.REQUIRED_SHIFT, DragType.OPEN_SHIFT, DragType.SCHEDULE],
};

export const moveZoneSupport: Record<DragType, DragType[]> = {
  [DragType.SHIFT]: [],
  [DragType.EVENT]: [],
  [DragType.SCHEDULE]: [DragType.SCHEDULE, DragType.OPEN_SHIFT],
  [DragType.OPEN_SHIFT]: [DragType.SCHEDULE, DragType.OPEN_SHIFT],
  [DragType.REQUIRED_SHIFT]: [DragType.REQUIRED_SHIFT],
};

@Injectable({ providedIn: 'root' })
export class DragDropService {
  private config = inject(DragDropConfig);
  private scheduleActionService = inject(ScheduleNActionService);

  get lastEnterElement() {
    return this._lastEnterElement;
  }

  set lastEnterElement(value) {
    this._lastEnterElement = value;
    if (this._lastEnterElement) {
      const hasCopyZone = this.hasCopyZone();
      const hasMoveZone = this.hasMoveZone();

      if (!hasCopyZone) {
        this._lastEnterElement.children[0].classList.add(this.config.hiddenDropZone);
      }

      if (!hasMoveZone) {
        this._lastEnterElement.children[1].classList.add(this.config.hiddenDropZone);
      }

      if (!hasCopyZone || !hasMoveZone) {
        return;
      }

      this._lastEnterElement.children[0].classList.remove(this.config.hiddenDropZone);
      this._lastEnterElement.children[1].classList.remove(this.config.hiddenDropZone);
    }
  }

  allowedDropZones: string[] = [];
  onDragSuccessCallback: EventEmitter<DragDropData>;
  dragData: any;
  private _lastEnterElement: HTMLElement | null | undefined;

  isDragging: EventEmitter<boolean> = new EventEmitter<boolean>();
  private _isDragged: boolean;

  get isDragged() {
    return this._isDragged;
  }

  set isDragged(isDrag: boolean) {
    if (!isDrag) {
      this.resetLastElement();
      this.lastEnterElement = null;
      this.scheduleActionService.isCopyingSubject.next(false);
      this.scheduleActionService.isDragging.next(null);
    }
    this._isDragged = isDrag;
    this.isDragging.emit(isDrag);
  }

  resetLastElement(includeSelf: boolean = true) {
    if (!this.lastEnterElement) {
      return;
    }

    if (includeSelf) {
      this.lastEnterElement.classList.remove(this.config.onDropEntered);
      this.lastEnterElement.classList.remove(this.config.onDragDeniedClass);
    }

    for (let i = 0; i < this.lastEnterElement.children.length; i++) {
      this.lastEnterElement.children[i].classList.remove(this.config.onDropEntered);
      this.lastEnterElement.children[i].classList.remove(this.config.hiddenDropZone);
    }
  }

  hasCopyZone(): boolean {
    const dragType: DragType = this.dragData.type;
    const dropZoneData = this.getDataSet();

    if (!dropZoneData) {
      return false;
    }

    return copyZoneSupport[dragType]?.includes(dropZoneData.dropZoneType);
  }

  hasMoveZone(): boolean {
    const dragType: DragType = this.dragData.type;
    const dropZoneData = this.getDataSet();

    if (!dropZoneData) {
      return false;
    }

    return moveZoneSupport[dragType]?.includes(dropZoneData.dropZoneType);
  }
  hasSkillsWarning(): boolean {
    const dropZoneData = this.getDataSet();
    if (!dropZoneData) {
      return false;
    }

    const dragDropData = {
      dragData: this.dragData,
      dropZoneData: dropZoneData,
    };
    return this.scheduleActionService.hasSkillWarning(dragDropData);
  }

  getDataSet(): DropZoneData | null {
    if (!this.lastEnterElement) {
      return null;
    }

    const dataset = this.lastEnterElement.dataset;
    return {
      ...dataset,
      date: dataset['date'] ?? '',
      employeeEndDate: dataset['employeeEndDate'] ?? null,
      dropZoneType: parseInt(dataset['dropZoneType'] ?? '', 10),
    };
  }

  isDropAllowed() {
    const dropZoneData = this.getDataSet();
    if (!dropZoneData) {
      return false;
    }

    const dragDropData = {
      dragData: this.dragData,
      dropZoneData: dropZoneData,
    };
    switch (dropZoneData.dropZoneType) {
      case DragType.SCHEDULE:
        return this.scheduleActionService.allowDropForScheduleRow()(dragDropData);
      case DragType.OPEN_SHIFT:
        return this.scheduleActionService.allowDropForOpenShiftRow(dragDropData);
      case DragType.REQUIRED_SHIFT:
        return this.scheduleActionService.allowDropForRequiredShiftRow(dragDropData);
      case DragType.EVENT:
        return this.scheduleActionService.allowDropForEventRow(dragDropData);
    }

    return false;
  }
}
